import { acceptRemoteStages } from '../edition/index.js';
import { api } from './api.js';

/**
 * Keeps the operator's per-game stages current without a rebuild (docs/proposals/remote-game-stages.md). The ORDER of trust is:
 *   the last good payload from the network, else the cached copy on this device, else the stages this build was made with.
 * The app must never fail, or hide a game, because the endpoint is down: every failure path below ends quietly in the next fallback.
 * It never throws, and it only ever changes the STAGE of a game that is already in this build (`acceptRemoteStages` enforces that).
 */
export const GAME_STAGES_CACHE_KEY = 's4k:game-stages';
/** A cached copy older than this is ignored, so a long-offline device does not keep an old operator decision forever. */
export const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Ask again when the tab comes back to the foreground, but not more often than this. */
export const REFRESH_AFTER_MS = 10 * 60 * 1000;

interface CachedStages {
  readonly stages: Record<string, string>;
  readonly savedAt: number;
}

function readCache(now: number): CachedStages | null {
  try {
    const raw = localStorage.getItem(GAME_STAGES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedStages> | null;
    if (!parsed || typeof parsed.savedAt !== 'number' || typeof parsed.stages !== 'object' || parsed.stages === null) return null;
    if (now - parsed.savedAt > CACHE_MAX_AGE_MS || parsed.savedAt > now + 60_000) return null;
    return { stages: parsed.stages as Record<string, string>, savedAt: parsed.savedAt };
  } catch {
    return null; // storage blocked or the value is not JSON: act as if there were no cache
  }
}

function writeCache(stages: Record<string, string>, now: number): void {
  try {
    localStorage.setItem(GAME_STAGES_CACHE_KEY, JSON.stringify({ stages, savedAt: now } satisfies CachedStages));
  } catch {
    // storage blocked or full: the stages still apply for this visit
  }
}

/**
 * Synchronous, before the first render: apply the cached copy (re-validated like any payload, so a poisoned cache cannot add a game) so a
 * game the operator hid does not flash on screen while the network answers. Returns whether a cache was applied.
 */
export function primeGameStagesFromCache(now: number = Date.now()): boolean {
  const cached = readCache(now);
  if (!cached) return false;
  return acceptRemoteStages({ stages: cached.stages }) !== null;
}

export type GameStagesSource = 'remote' | 'cache' | 'build' | 'skipped';

/**
 * Fetch the stages and apply them. `remote` = the network answered with a valid payload (and it is now cached); `cache` = the network
 * failed or answered nonsense and the cached copy stands; `build` = neither, so the build's own stages stand; `skipped` = no network
 * call is made in this mode (`?engine=fake`).
 */
export async function loadGameStages(
  deps: { now?: () => number; fetchStages?: () => Promise<unknown | null> } = {},
): Promise<GameStagesSource> {
  const now = deps.now ?? Date.now;
  const fetchStages = deps.fetchStages ?? (() => api.getGameStages());
  let payload: unknown;
  try {
    payload = await fetchStages();
  } catch {
    return readCache(now()) ? 'cache' : 'build'; // offline, 5xx, bad JSON: whatever is already applied stays
  }
  if (payload === null) return 'skipped';
  const accepted = acceptRemoteStages(payload);
  if (accepted === null) return readCache(now()) ? 'cache' : 'build'; // wrong shape: ignored whole
  writeCache(accepted, now());
  return 'remote';
}

/**
 * Start keeping the stages current: one fetch now (the cache was already applied by `primeGameStagesFromCache`), and another when the tab
 * becomes visible again, at most every `REFRESH_AFTER_MS`. Returns a function that stops listening (for tests).
 */
export function startGameStagesSync(
  deps: { now?: () => number; fetchStages?: () => Promise<unknown | null> } = {},
): () => void {
  const now = deps.now ?? Date.now;
  let lastAttempt = now();
  void loadGameStages(deps);
  if (typeof document === 'undefined') return () => {};
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    if (now() - lastAttempt < REFRESH_AFTER_MS) return;
    lastAttempt = now();
    void loadGameStages(deps);
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}
